import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ResponsiveModal } from '@estee/elc-responsive-modal';
import { RemoveIcon, SpinnerIcon } from '@estee/elc-icons';
import styled from 'styled-components';
import { useTranslation } from '~sharedComponents/Providers/Translations/TranslationsHook';
import { classes } from '~utils/ClassNames';

export interface IModal {
    isModalOpen: boolean;
    closeModal(): void;
    children: React.ReactNode;
    maskClassName?: string;
    className?: string;
}

interface IStyledModalProps {
    childrenLoaded: boolean;
}

const StyledResponsiveModal = styled(ResponsiveModal)<IStyledModalProps>`
    min-width: ${({ childrenLoaded }) => (childrenLoaded ? 'auto' : '50%')};
`;

const StyledCloseButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
`;

const StyledContentWrapper = styled.div`
    max-height: 90dvh;
    overflow-y: auto;
`;

const StyledSpinnerIcon = styled(SpinnerIcon)`
    margin-block: 1rem 2rem;
    margin-inline: auto;
`;

const modalClass = 'cart-page-modal';
const closeButtonWrapperClass = classes(`${modalClass}__close-button-wrapper`);
const closeButtonClass = classes(`${modalClass}__close-button`);
const contentClass = classes(`${modalClass}__content`);

const translations = ['close'];

export const Modal = observer((props: IModal) => {
    const { isModalOpen, closeModal, children, maskClassName, className } = props;
    const contentRef = useRef<HTMLDivElement>(null);
    const [childrenLoaded, setChildrenLoaded] = useState(false);
    const { close } = useTranslation(translations);
    const modalWrapperClasses = classes(modalClass, className);
    const maskClasses = classes(`${modalClass}-mask`, maskClassName);

    useEffect(() => {
        const handleContentLoaded = () => {
            if (contentRef.current && contentRef.current.innerHTML.trim() !== '') {
                setChildrenLoaded(true);
            } else {
                setChildrenLoaded(false);
                setTimeout(handleContentLoaded, 100);
            }
        };

        handleContentLoaded();
    }, [isModalOpen]);

    return (
        <StyledResponsiveModal
            isOpen={isModalOpen}
            onMaskClick={closeModal}
            className={modalWrapperClasses}
            maskClassName={maskClasses}
            childrenLoaded={childrenLoaded}
        >
            <StyledCloseButtonWrapper className={closeButtonWrapperClass}>
                <button className={closeButtonClass} onClick={closeModal} aria-label={close}>
                    <RemoveIcon />
                </button>
            </StyledCloseButtonWrapper>
            <StyledContentWrapper className={contentClass} ref={contentRef}>
                {children}
            </StyledContentWrapper>
            {!childrenLoaded && <StyledSpinnerIcon />}
        </StyledResponsiveModal>
    );
});
